import type { ReactNode } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/utils/cn'
import { SAFE_AREA_X } from '../recipes'

/** The grab handle at the top of a phone bottom sheet (decorative). */
export function GrabHandle() {
  return (
    <span
      aria-hidden
      className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-muted-foreground/50"
    />
  )
}

export interface ResponsiveModalOptions {
  /**
   * `sheet` = phone bottom sheet, `dialog` = desktop dialog, `panel` = desktop side panel. The
   * caller picks it by size.
   */
  variant: 'sheet' | 'dialog' | 'panel'
  /** Accessible name. Visually hidden: the content draws its own heading. */
  title: string
  /** Size classes per variant, e.g. `{ sheet: 'data-[side=bottom]:h-[92dvh]', dialog: 'w-[1120px]' }`. */
  size?: { sheet?: string; dialog?: string; panel?: string }
  /** E.g. keep a zoomed body map open: `event.preventDefault()`. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void
}

export interface ResponsiveModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  modal: ResponsiveModalOptions
  /** The pure panel; give its root `@container/modal` so the modal recipes apply. */
  children: ReactNode
}

const SURFACE = 'gap-0 bg-popover p-0 text-popover-foreground'

/**
 * The HUMINT modal: a shadcn bottom Sheet with a grab handle on the phone, a Dialog or a side
 * panel on the desktop, around the same pure panel. No built-in close button (the panel has its
 * own) and no description. Backdrop, Escape and the panel's close all go through
 * `onOpenChange(false)`.
 */
export function ResponsiveModal({ open, onOpenChange, modal, children }: ResponsiveModalProps) {
  const { variant, title, size, onEscapeKeyDown } = modal
  if (variant === 'dialog') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          onEscapeKeyDown={onEscapeKeyDown}
          className={cn('flex flex-col', SURFACE, size?.dialog)}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          {children}
        </DialogContent>
      </Dialog>
    )
  }
  const panel = variant === 'panel'
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={panel ? 'right' : 'bottom'}
        showCloseButton={false}
        aria-describedby={undefined}
        onEscapeKeyDown={onEscapeKeyDown}
        className={cn(
          !panel && 'rounded-t-2xl',
          'border-border',
          SURFACE,
          !panel && SAFE_AREA_X,
          panel ? size?.panel : size?.sheet
        )}
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {!panel && <GrabHandle />}
        {children}
      </SheetContent>
    </Sheet>
  )
}

/**
 * The sizes of a HUMINT panel's modal: the bottom sheet leaves the top of the screen visible so it
 * still reads as a sheet; the dialog stays above the panel's 48 rem two-column breakpoint
 * (`@3xl/modal`); the side panel is one column.
 */
const PANEL_SIZE = {
  sheet: 'max-h-[92dvh]',
  dialog: 'max-h-[90dvh] w-full max-w-[min(60rem,calc(100%-2rem))]!',
  panel: 'w-full sm:max-w-md',
}

export interface PanelModalProps {
  open: boolean
  /** The variant and the accessible name; the size defaults to `PANEL_SIZE`. */
  modal: ResponsiveModalOptions
  /** Backdrop and Escape, which the host may refuse while closing is not safe. */
  onDismiss: () => void
  children: ReactNode
}

/** A HUMINT panel in its modal (the enrol, *Add photo*, encounter and mark sheets). */
export function PanelModal({ open, modal, onDismiss, children }: PanelModalProps) {
  return (
    <ResponsiveModal
      open={open}
      onOpenChange={next => {
        if (!next) onDismiss()
      }}
      modal={{ size: PANEL_SIZE, ...modal }}
    >
      {children}
    </ResponsiveModal>
  )
}
